import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from "@mui/material";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useState, useEffect, useCallback } from "react";
import { useDebounce } from "@uidotdev/usehooks";

import { useAdminSender } from "../api";
import { PlaceDetailsResponse, SpotBusinessStatus } from "../generatedApi/spotManagerApi";
import { FieldSelection } from "./GooglePlaceDetailsModal/FieldSelection";
import { SkuSummary } from "./GooglePlaceDetailsModal/SkuSummary";
import { calculateRequiredTiers, convertToLocalPhoneNumber } from "./GooglePlaceDetailsModal/utils";
import type { SelectedFields } from "./GooglePlaceDetailsModal/types";
import { DEFAULT_REQUIRED_FIELDS } from "./GooglePlaceDetailsModal/types";
import { MapCenterTracker } from "./MapCenterTracker";
import { MapBoundsUpdater } from "./MapBoundsUpdater";
import { DefaultIcon, HighlightIcon, DuplicateIcon } from "./MapMarkerIcons";
import { DEFAULT_CENTER } from "../constants/leafletMap";
import { generateSpotId } from "../utils/spotIdGenerator";
import { useDuplicateCheck, type PlaceWithDuplicateCheck } from "../hooks/useDuplicateCheck";

const SpotCreateByPlaceSearch = () => {
  const {
    sender: searchPlaces,
    isLoading: isSearching,
    error: searchError
  } = useAdminSender({
    apiName: "spot_manager",
    serviceName: "googlePlaces",
    operationId: "searchPlaces"
  });

  const { sender: searchDraftSpots } = useAdminSender({
    apiName: "admin_search_engine",
    serviceName: "default",
    operationId: "searchDraftSpots"
  });

  const { checkPlacesBatch } = useDuplicateCheck();

  const {
    sender: getPlaceDetails,
    isLoading: isLoadingDetails,
    error: detailsError
  } = useAdminSender({
    apiName: "spot_manager",
    serviceName: "googlePlaces",
    operationId: "getPlaceDetails"
  });

  const {
    sender: createSpotItem,
    isLoading: isCreatingSpot,
    error: createSpotError
  } = useAdminSender({
    apiName: "spot_manager",
    serviceName: "default",
    operationId: "createSpotItem"
  });

  const {
    sender: createSpotRevision,
    isLoading: isCreatingRevision,
    error: createRevisionError
  } = useAdminSender({
    apiName: "spot_manager",
    serviceName: "default",
    operationId: "createSpotRevision"
  });

  // ウィザード全体のstate
  const [activeStep, setActiveStep] = useState(0);

  // Step 1用のstate
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState<PlaceWithDuplicateCheck[]>([]);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const [hoveredPlaceId, setHoveredPlaceId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lon: number }>({
    lat: DEFAULT_CENTER[0],
    lon: DEFAULT_CENTER[1]
  });

  // 地図の中心座標更新用のコールバック（useCallbackで安定化）
  const handleMapCenterChange = useCallback((lat: number, lon: number) => {
    setMapCenter({ lat, lon });
  }, []);

  // Step 2用のstate
  const [placeDetails, setPlaceDetails] = useState<PlaceDetailsResponse | null>(null);
  const [selectedFields, setSelectedFields] = useState<SelectedFields>(DEFAULT_REQUIRED_FIELDS);
  const [fieldSelectionError, setFieldSelectionError] = useState<string | null>(null);

  // Step 3用のstate
  const [spotName, setSpotName] = useState("");
  const [spotId, setSpotId] = useState("");
  const [createdSpotId, setCreatedSpotId] = useState<string | null>(null);
  const [spotIdCheckResult, setSpotIdCheckResult] = useState<{
    isChecking: boolean;
    isDuplicate: boolean;
    existingSpotId?: string;
  }>({ isChecking: false, isDuplicate: false });

  // スポットID重複チェック用のデバウンス（300ms）
  const debouncedSpotId = useDebounce(spotId, 300);

  const steps = ["地図検索", "詳細情報取得", "スポット作成"];

  // Google Places APIのbusiness_status（string）をSpotBusinessStatus（enum）に変換
  const convertBusinessStatus = (status?: string | null): SpotBusinessStatus | undefined => {
    if (!status) return undefined;

    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "operational":
        return SpotBusinessStatus.OPERATIONAL;
      case "closed_temporarily":
        return SpotBusinessStatus.CLOSED_TEMPORARILY;
      case "closed_permanently":
        return SpotBusinessStatus.CLOSED_PERMANENTLY;
      default:
        // 不明な値の場合はundefinedを返す
        console.warn(`Unknown business_status: ${status}`);
        return undefined;
    }
  };

  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      alert("検索キーワードを入力してください");
      return;
    }

    setSearchResults([]);

    try {
      const result = await searchPlaces({
        requestBody: {
          query: searchKeyword,
          location_bias: {
            lat: mapCenter.lat,
            lon: mapCenter.lon
          }
        }
      });

      if (result && result.places) {
        // 検索結果を表示（重複チェック中）
        const placesWithCheckingFlag = result.places.map((p) => ({
          ...p,
          isDuplicateChecking: true
        }));
        setSearchResults(placesWithCheckingFlag);

        // 重複チェック実行
        const checkedPlaces = await checkPlacesBatch(result.places);
        setSearchResults(checkedPlaces);
      }
    } catch (error) {
      console.error("Search failed:", error);
      // searchErrorステートによりエラーが表示される
    }
  };

  const handleSelectPlace = (placeId: string) => {
    setSelectedPlaceId(placeId);
    setActiveStep(1); // Step 2へ進む
  };

  // 詳細情報取得ボタンのハンドラー
  const handleFetchDetails = async () => {
    if (selectedPlaceId) {
      // 選択されたフィールドから必要なSKUティアを計算
      const tiers = calculateRequiredTiers(selectedFields);

      if (tiers.length === 0) {
        // 少なくとも1つのフィールドを選択する必要がある
        setFieldSelectionError("少なくとも1つのフィールドを選択してください");
        return;
      }

      // エラーをクリア
      setFieldSelectionError(null);

      const details = await getPlaceDetails({
        placeId: selectedPlaceId,
        fields: tiers.join(",")
      });
      if (details) {
        setPlaceDetails(details);
      }
    }
  };

  // Step 3への遷移時にフォームを初期化
  const handleMoveToStep3 = () => {
    if (placeDetails) {
      setSpotName(placeDetails.name || "");
      setSpotId(generateSpotId());
      setSpotIdCheckResult({ isChecking: false, isDuplicate: false });
      setActiveStep(2);
    }
  };

  // スポット作成ボタンの有効/無効判定
  const isCreateButtonDisabled = () => {
    return (
      !spotName.trim() || // スポット名が未入力
      !spotId.trim() || // スポットIDが未入力
      spotIdCheckResult.isChecking || // スポットID重複チェック中
      spotIdCheckResult.isDuplicate || // スポットIDが重複
      isCreatingSpot || // スポット作成中
      isCreatingRevision // リビジョン作成中
    );
  };

  // スポットID重複チェック（useDebounceを使用）
  useEffect(() => {
    if (!debouncedSpotId || debouncedSpotId.length === 0) {
      setSpotIdCheckResult({ isChecking: false, isDuplicate: false });
      return;
    }

    const checkSpotIdDuplicate = async () => {
      setSpotIdCheckResult({ isChecking: true, isDuplicate: false });

      try {
        const result = await searchDraftSpots({
          requestBody: {
            spot_id: debouncedSpotId,
            size: 1
          }
        });

        if (result && result.results && result.results.length > 0) {
          setSpotIdCheckResult({
            isChecking: false,
            isDuplicate: true,
            existingSpotId: result.results[0].spot_id
          });
        } else {
          setSpotIdCheckResult({ isChecking: false, isDuplicate: false });
        }
      } catch (error) {
        console.error("スポットID重複チェックエラー:", error);
        setSpotIdCheckResult({ isChecking: false, isDuplicate: false });
      }
    };

    checkSpotIdDuplicate();
  }, [debouncedSpotId, searchDraftSpots]);

  // スポット作成処理
  const handleCreateSpot = async () => {
    if (!spotName.trim() || !spotId.trim() || !placeDetails) {
      alert("必須項目を入力してください");
      return;
    }

    try {
      // 1. スポットメタデータ作成
      const spotMetadata = await createSpotItem({
        requestBody: {
          spot_id: spotId,
          name: spotName
        }
      });

      if (!spotMetadata) {
        throw new Error("スポットメタデータの作成に失敗しました");
      }

      // 2. 初期リビジョン作成
      const revision = await createSpotRevision({
        spotId: spotId,
        requestBody: {
          spot_id: spotId,
          name: spotName,
          location: placeDetails.location
            ? { lat: placeDetails.location.lat, lon: placeDetails.location.lon }
            : undefined,
          address: placeDetails.address || undefined,
          address_component: placeDetails.address_component || undefined,
          phone_number: placeDetails.phone_number
            ? convertToLocalPhoneNumber(placeDetails.phone_number)
            : undefined,
          opening_hours: placeDetails.opening_hours?.[0] || undefined,
          business_status: convertBusinessStatus(placeDetails.business_status),
          google_place_id: placeDetails.place_id,
          google_cid: placeDetails.google_cid || undefined,
          rating:
            placeDetails.rating && placeDetails.rating.rating !== null
              ? [
                  {
                    source_name: "googlemaps", // Google Maps由来の評価は固定値
                    rating: placeDetails.rating.rating,
                    rating_count: placeDetails.rating.rating_count || undefined
                  }
                ]
              : undefined,
          xlinks: placeDetails.website ? [placeDetails.website] : undefined,
          parking: placeDetails.parking || undefined
        }
      });

      if (revision) {
        setCreatedSpotId(spotId);
        alert("スポットが正常に作成されました！");
      }
    } catch (error) {
      console.error("Spot creation error:", error);
      alert("スポットの作成に失敗しました");
    }
  };

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        スポット新規作成（地図検索）
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mt: 3, mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Stack spacing={3} sx={{ mt: 3 }}>
        {activeStep === 0 && (
          <>
            <Typography variant="h6">Step 1: 地図検索</Typography>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSearch();
              }}
            >
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  label="検索キーワード（例: 名古屋 カフェ）"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  required
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSearching}
                  sx={{ minWidth: 120 }}
                >
                  {isSearching ? <CircularProgress size={24} /> : "検索"}
                </Button>
              </Stack>
            </form>

            {searchError && <Alert severity="error">検索に失敗しました</Alert>}

            {searchResults.length > 0 && (
              <Typography variant="body2" color="success.main">
                {searchResults.length}件の結果が見つかりました
              </Typography>
            )}

            <Box
              sx={{ height: 500, width: "100%", mb: 3 }}
              role="region"
              aria-label="スポット検索結果の地図表示"
            >
              <MapContainer
                center={DEFAULT_CENTER}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
                aria-label="検索結果マップ"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapBoundsUpdater searchResults={searchResults} />
                <MapCenterTracker onCenterChange={handleMapCenterChange} />
                {searchResults.map((place) => {
                  // ホバー時はハイライトアイコン、それ以外は通常のアイコン
                  const isHovered = hoveredPlaceId === place.place_id;
                  const markerIcon = isHovered
                    ? HighlightIcon
                    : place.existingSpotId
                      ? DuplicateIcon
                      : DefaultIcon;

                  return place.location ? (
                    <Marker
                      key={place.place_id}
                      position={[place.location.lat, place.location.lon]}
                      icon={markerIcon}
                      eventHandlers={{
                        mouseover: () => setHoveredPlaceId(place.place_id),
                        mouseout: () => setHoveredPlaceId(null)
                      }}
                    >
                      <Popup>
                        <div>
                          <strong>{place.name}</strong>
                          <br />
                          <span style={{ fontSize: "0.875rem", color: "#666" }}>
                            {place.address || "住所情報なし"}
                          </span>
                          {place.existingSpotId && (
                            <>
                              <br />
                              <span style={{ fontSize: "0.75rem", color: "#757575" }}>
                                登録済み: {place.existingSpotId}
                              </span>
                            </>
                          )}
                          <br />
                          <br />
                          {place.existingSpotId ? (
                            <Button
                              size="small"
                              variant="outlined"
                              href={`/spots/${place.existingSpotId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`既存スポット ${place.existingSpotId} の編集画面へ移動`}
                            >
                              編集画面へ
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleSelectPlace(place.place_id)}
                              disabled={place.isDuplicateChecking}
                              aria-label={
                                place.isDuplicateChecking ? "重複確認中" : "このスポットを選択"
                              }
                            >
                              このスポットを選択
                            </Button>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ) : null;
                })}
              </MapContainer>
            </Box>

            {searchResults.length === 0 && !isSearching && (
              <Typography variant="body2" color="text.secondary">
                検索キーワードを入力して検索してください
              </Typography>
            )}

            {searchResults.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  検索結果一覧
                </Typography>
                <List sx={{ border: (theme) => `1px solid ${theme.palette.divider}` }}>
                  {searchResults.map((place, index) => (
                    <Box
                      key={place.place_id}
                      onMouseEnter={() => setHoveredPlaceId(place.place_id)}
                      onMouseLeave={() => setHoveredPlaceId(null)}
                      sx={{
                        backgroundColor:
                          hoveredPlaceId === place.place_id ? "action.hover" : "transparent"
                      }}
                    >
                      {index > 0 && <Divider />}
                      <ListItem>
                        <Stack
                          width="100%"
                          spacing={1}
                          direction="row"
                          justifyContent="space-between"
                        >
                          <CardContent>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="h6">{place.name}</Typography>
                              {place.isDuplicateChecking && (
                                <Chip label="重複確認中..." size="small" color="default" />
                              )}
                              {!place.isDuplicateChecking && place.existingSpotId && (
                                <Chip label="登録済み" size="small" color="default" />
                              )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {place.address || "住所情報なし"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              place_id: {place.place_id}
                            </Typography>
                            {place.existingSpotId && (
                              <Typography variant="caption" color="text.disabled" display="block">
                                既に登録されているスポット: {place.existingSpotId}
                              </Typography>
                            )}
                          </CardContent>
                          <CardActions>
                            {place.existingSpotId ? (
                              <Button
                                size="small"
                                variant="outlined"
                                component={Link}
                                to={`/spots/${place.existingSpotId}`}
                              >
                                編集画面へ
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleSelectPlace(place.place_id)}
                                disabled={place.isDuplicateChecking}
                              >
                                このスポットを選択
                              </Button>
                            )}
                          </CardActions>
                        </Stack>
                      </ListItem>
                    </Box>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 2: 詳細情報取得
            </Typography>

            {/* 1. 選択したスポット名 */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                選択したスポット名
              </Typography>
              <Typography variant="body1">
                {searchResults.find((p) => p.place_id === selectedPlaceId)?.name || "-"}
              </Typography>
            </Box>

            {/* 2. place_id */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary">
                place_id
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {selectedPlaceId}
              </Typography>
            </Box>

            {/* 3. 取得フィールド選択 */}
            <Box sx={{ mb: 3 }}>
              <FieldSelection
                selectedFields={selectedFields}
                onChange={(field, checked) => {
                  setSelectedFields((prev) => ({
                    ...prev,
                    [field]: checked
                  }));
                }}
              />
            </Box>

            {/* 4. SKUコスト試算 */}
            <Box sx={{ mb: 3 }}>
              <SkuSummary selectedFields={selectedFields} />
            </Box>

            {/* 5. 詳細情報取得ボタン */}
            <Button
              variant="contained"
              onClick={handleFetchDetails}
              disabled={isLoadingDetails}
              sx={{ mb: 3 }}
            >
              {isLoadingDetails ? <CircularProgress size={20} /> : "詳細情報を取得"}
            </Button>

            {fieldSelectionError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {fieldSelectionError}
              </Alert>
            )}

            {detailsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                詳細情報の取得に失敗しました
              </Alert>
            )}

            {/* 4. 取得結果プレビュー（テーブル形式） */}
            {placeDetails && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
                  取得結果プレビュー
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Box display="flex" borderBottom="1px solid #e0e0e0" pb={1}>
                        <Typography variant="body2" sx={{ width: "200px", fontWeight: "bold" }}>
                          項目
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          値
                        </Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          スポット名
                        </Typography>
                        <Typography variant="body2">{placeDetails.name || "-"}</Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          住所
                        </Typography>
                        <Typography variant="body2">{placeDetails.address || "-"}</Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          住所コンポーネント
                        </Typography>
                        <Typography variant="body2">
                          {placeDetails.address_component
                            ? `${placeDetails.address_component.pref || ""} ${placeDetails.address_component.city || ""} (郵便番号: ${placeDetails.address_component.postal_code || "-"})`
                            : "-"}
                        </Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          位置情報
                        </Typography>
                        <Typography variant="body2">
                          {placeDetails.location
                            ? `緯度: ${placeDetails.location.lat}, 経度: ${placeDetails.location.lon}`
                            : "-"}
                        </Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          電話番号
                        </Typography>
                        <Typography variant="body2">
                          {placeDetails.phone_number
                            ? convertToLocalPhoneNumber(placeDetails.phone_number)
                            : "-"}
                        </Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          営業時間
                        </Typography>
                        <Typography variant="body2">
                          {placeDetails.opening_hours && placeDetails.opening_hours.length > 0
                            ? placeDetails.opening_hours.join(", ")
                            : "-"}
                        </Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          ウェブサイト
                        </Typography>
                        <Typography variant="body2">
                          {placeDetails.website ? (
                            <a
                              href={placeDetails.website}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {placeDetails.website}
                            </a>
                          ) : (
                            "-"
                          )}
                        </Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          評価
                        </Typography>
                        <Typography variant="body2">
                          {placeDetails.rating
                            ? `${placeDetails.rating.rating} / 5.0 (${placeDetails.rating.rating_count}件)`
                            : "-"}
                        </Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          営業状態
                        </Typography>
                        <Typography variant="body2">
                          {placeDetails.business_status || "-"}
                        </Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          google_cid
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          {placeDetails.google_cid || "-"}
                        </Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          駐車場情報
                        </Typography>
                        <Typography variant="body2">{placeDetails.parking || "-"}</Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" sx={{ width: "200px" }}>
                          キャッシュヒット
                        </Typography>
                        <Typography variant="body2">
                          {placeDetails.cache_hit !== undefined
                            ? placeDetails.cache_hit
                              ? "はい"
                              : "いいえ"
                            : "-"}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* 5. 次へボタン */}
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  setActiveStep(0);
                  setPlaceDetails(null);
                }}
              >
                戻る
              </Button>
              <Button variant="contained" onClick={handleMoveToStep3} disabled={!placeDetails}>
                次へ
              </Button>
            </Stack>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 3: スポット作成
            </Typography>

            {/* エラー表示 */}
            {(createSpotError || createRevisionError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                スポット作成に失敗しました:{" "}
                {createSpotError?.message || createRevisionError?.message}
              </Alert>
            )}

            {/* 成功表示 */}
            {createdSpotId && (
              <Alert severity="success" sx={{ mb: 2 }}>
                スポットを作成しました！{" "}
                <Link to={`/spots/${createdSpotId}`} target="_blank" rel="noopener noreferrer">
                  編集画面を開く
                </Link>
              </Alert>
            )}

            {!createdSpotId && (
              <Stack spacing={3}>
                {/* 1. スポット名（編集可能） */}
                <TextField
                  label="スポット名"
                  value={spotName}
                  onChange={(e) => setSpotName(e.target.value)}
                  required
                  fullWidth
                />

                {/* 2. スポットID（自動生成、編集可能） */}
                <Box>
                  <TextField
                    label="スポットID"
                    value={spotId}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setSpotId(value);
                    }}
                    required
                    fullWidth
                    error={spotIdCheckResult.isDuplicate}
                  />
                  {spotIdCheckResult.isChecking && (
                    <Box display="flex" alignItems="center" mt={1}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        重複チェック中...
                      </Typography>
                    </Box>
                  )}
                  {spotIdCheckResult.isDuplicate && spotIdCheckResult.existingSpotId && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      このスポットIDは既に存在します。{" "}
                      <Link to={`/spots/${spotIdCheckResult.existingSpotId}`} target="_blank">
                        既存のスポット「{spotIdCheckResult.existingSpotId}」を編集
                      </Link>
                    </Alert>
                  )}
                </Box>

                {/* 3. Google API取得情報（表示のみ） */}
                {placeDetails && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
                        Google API取得情報
                      </Typography>
                      <Stack spacing={1.5}>
                        <Box display="flex">
                          <Typography variant="body2" sx={{ width: "200px" }}>
                            住所
                          </Typography>
                          <Typography variant="body2">{placeDetails.address || "-"}</Typography>
                        </Box>
                        <Box display="flex">
                          <Typography variant="body2" sx={{ width: "200px" }}>
                            電話番号
                          </Typography>
                          <Typography variant="body2">
                            {placeDetails.phone_number
                              ? convertToLocalPhoneNumber(placeDetails.phone_number)
                              : "-"}
                          </Typography>
                        </Box>
                        <Box display="flex">
                          <Typography variant="body2" sx={{ width: "200px" }}>
                            place_id
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {placeDetails.place_id}
                          </Typography>
                        </Box>
                        <Box display="flex">
                          <Typography variant="body2" sx={{ width: "200px" }}>
                            google_cid
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {placeDetails.google_cid || "-"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* 4. 作成ボタン */}
                <Stack direction="row" spacing={2}>
                  <Button variant="outlined" onClick={() => setActiveStep(1)}>
                    戻る
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleCreateSpot}
                    disabled={isCreateButtonDisabled()}
                  >
                    {isCreatingSpot || isCreatingRevision ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        作成中...
                      </>
                    ) : (
                      "作成"
                    )}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default SpotCreateByPlaceSearch;
